import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { AgentBoard, AgentFile } from "@/types/agent";
import { cn } from "@/lib/utils";
import { ImageIcon, LayoutDashboard, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createBoardCode, createMediaCode } from "./agentAssetCode";

interface InstructionsEditorProps {
  value: string;
  onChange: (value: string) => void;
  mediaFiles: AgentFile[];
  boards: AgentBoard[];
  onUploadMedia: () => void;
  placeholder?: string;
}

const MENTION_ATTR = "data-mention";
const MARKER_ATTR = "data-mention-marker";

type MentionOption =
  | { type: "media"; id: string; code: string; label: string }
  | { type: "board"; id: string; code: string; label: string }
  | { type: "upload"; id: "upload"; label: string };

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function createMentionSpan(code: string): HTMLSpanElement {
  const span = document.createElement("span");
  span.setAttribute(MENTION_ATTR, code);
  span.contentEditable = "false";
  span.className =
    "inline-flex items-center bg-blue-100/60 text-blue-600 rounded px-1 mx-0.5 font-mono text-sm select-none whitespace-nowrap";
  span.textContent = `@${code}`;
  return span;
}

function buildEditorDOM(
  text: string,
  mentionNames: Set<string>
): DocumentFragment {
  const fragment = document.createDocumentFragment();
  if (!text) return fragment;

  const sortedNames = Array.from(mentionNames).sort(
    (a, b) => b.length - a.length
  );
  const mentionPattern =
    sortedNames.length > 0
      ? new RegExp(
          `@(${sortedNames.map(escapeRegex).join("|")})(?![\\w.-])`,
          "g"
        )
      : null;

  const lines = text.split("\n");
  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      fragment.appendChild(document.createElement("br"));
    }

    if (!mentionPattern || !line.includes("@")) {
      if (line) fragment.appendChild(document.createTextNode(line));
      return;
    }

    let lastIdx = 0;
    mentionPattern.lastIndex = 0;
    let match;

    while ((match = mentionPattern.exec(line)) !== null) {
      if (match.index > lastIdx) {
        fragment.appendChild(
          document.createTextNode(line.slice(lastIdx, match.index))
        );
      }
      fragment.appendChild(createMentionSpan(match[1]));
      lastIdx = match.index + match[0].length;
    }

    if (lastIdx < line.length) {
      fragment.appendChild(document.createTextNode(line.slice(lastIdx)));
    }
  });

  return fragment;
}

function serializeEditor(container: HTMLElement): string {
  const parts: string[] = [];

  function processChildren(parent: Node) {
    const children = Array.from(parent.childNodes);
    children.forEach((child, index) => {
      if (child.nodeType === Node.TEXT_NODE) {
        parts.push((child.textContent || "").replace(/\u200B/g, ""));
      } else if (child instanceof HTMLElement) {
        if (child.hasAttribute(MARKER_ATTR)) return;
        if (child.hasAttribute(MENTION_ATTR)) {
          parts.push(child.textContent || "");
        } else if (child.tagName === "BR") {
          const isPlaceholder =
            child.parentElement !== container &&
            child.parentElement?.childNodes.length === 1;
          if (!isPlaceholder) {
            parts.push("\n");
          }
        } else {
          if (index > 0) parts.push("\n");
          processChildren(child);
        }
      }
    });
  }

  processChildren(container);
  return parts.join("");
}

function removeMarker(editor: HTMLElement | null) {
  editor?.querySelector(`[${MARKER_ATTR}]`)?.remove();
}

export const InstructionsEditor = ({
  value,
  onChange,
  mediaFiles,
  boards,
  onUploadMedia,
  placeholder = "Enter detailed instructions for the agent",
}: InstructionsEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const mentionRangeRef = useRef<Range | null>(null);
  const isInternalChange = useRef(false);
  const [showMention, setShowMention] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);

  const mentionCodes = useMemo(
    () =>
      new Set([
        ...mediaFiles.map((file) => file.code || createMediaCode(file.name)),
        ...boards.map((board) => board.code || createBoardCode(board.name)),
      ]),
    [boards, mediaFiles]
  );
  const mentionOptions = useMemo<MentionOption[]>(
    () => [
      ...mediaFiles.map((file) => ({
        type: "media" as const,
        id: file.path,
        code: file.code || createMediaCode(file.name),
        label: file.name,
      })),
      ...boards.map((board) => ({
        type: "board" as const,
        id: board.id,
        code: board.code || createBoardCode(board.name),
        label: board.name,
      })),
      { type: "upload", id: "upload", label: "Upload media" },
    ],
    [boards, mediaFiles]
  );

  useEffect(() => {
    if (!showMention) return;
    setSelectedOptionIndex(0);
  }, [showMention, mentionOptions.length]);

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    const editor = editorRef.current;
    if (!editor) return;
    editor.innerHTML = "";
    editor.appendChild(buildEditorDOM(value, mentionCodes));
  }, [value, mentionCodes]);

  const syncToState = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const serialized = serializeEditor(editor);
    isInternalChange.current = true;
    onChange(serialized);
  }, [onChange]);

  const focusEditor = useCallback(() => {
    editorRef.current?.focus();
  }, []);

  const handleInput = useCallback(() => {
    syncToState();
  }, [syncToState]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      range.deleteContents();

      const lines = text.split("\n");
      const fragment = document.createDocumentFragment();
      lines.forEach((line, i) => {
        if (i > 0) fragment.appendChild(document.createElement("br"));
        if (line) fragment.appendChild(document.createTextNode(line));
      });
      range.insertNode(fragment);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);

      syncToState();
    },
    [syncToState]
  );

  const insertMention = useCallback(
    (code: string) => {
      const editor = editorRef.current;
      if (!editor) return;

      const span = createMentionSpan(code);
      const spacer = document.createTextNode(" ");
      const marker = editor.querySelector(`[${MARKER_ATTR}]`);

      if (marker) {
        marker.replaceWith(span, spacer);
      } else if (mentionRangeRef.current) {
        const range = mentionRangeRef.current.cloneRange();
        range.deleteContents();
        range.insertNode(spacer);
        range.insertNode(span);
      } else {
        return;
      }

      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.setStartAfter(spacer);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      mentionRangeRef.current = null;
      setShowMention(false);
      syncToState();
      editor.focus();
    },
    [syncToState]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (showMention) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedOptionIndex((current) =>
            mentionOptions.length === 0
              ? 0
              : (current + 1) % mentionOptions.length
          );
          return;
        }

        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedOptionIndex((current) =>
            mentionOptions.length === 0
              ? 0
              : (current - 1 + mentionOptions.length) % mentionOptions.length
          );
          return;
        }

        if (e.key === "Enter") {
          e.preventDefault();
          const selectedOption = mentionOptions[selectedOptionIndex];
          if (!selectedOption) return;

          if (
            selectedOption.type === "media" ||
            selectedOption.type === "board"
          ) {
            insertMention(selectedOption.code);
          } else {
            setShowMention(false);
            removeMarker(editorRef.current);
            mentionRangeRef.current = null;
            onUploadMedia();
          }
          return;
        }

        if (e.key === "Escape") {
          e.preventDefault();
          setShowMention(false);
          return;
        }
      }

      if (e.key === "@" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);

          const marker = document.createElement("span");
          marker.setAttribute(MARKER_ATTR, "");
          range.insertNode(marker);
          range.setStartAfter(marker);
          range.collapse(true);
          mentionRangeRef.current = range.cloneRange();

          const rect = marker.getBoundingClientRect();
          const editorRect = editorRef.current?.getBoundingClientRect();
          if (editorRect && anchorRef.current) {
            anchorRef.current.style.top = `${rect.bottom - editorRect.top}px`;
            anchorRef.current.style.left = `${rect.left - editorRect.left}px`;
          }
        }
        setShowMention(true);
        requestAnimationFrame(() => {
          focusEditor();
        });
        return;
      }

      if (e.key === "Backspace") {
        const selection = window.getSelection();
        if (selection && selection.isCollapsed && selection.rangeCount > 0) {
          const { startContainer: node, startOffset: offset } =
            selection.getRangeAt(0);
          let target: HTMLElement | null = null;

          if (node.nodeType === Node.TEXT_NODE && offset === 0) {
            const prev = node.previousSibling;
            if (
              prev instanceof HTMLElement &&
              prev.hasAttribute(MENTION_ATTR)
            )
              target = prev;
          } else if (node.nodeType === Node.ELEMENT_NODE && offset > 0) {
            const prev = node.childNodes[offset - 1];
            if (
              prev instanceof HTMLElement &&
              prev.hasAttribute(MENTION_ATTR)
            )
              target = prev;
          }

          if (target) {
            e.preventDefault();
            target.remove();
            syncToState();
          }
        }
      }

      if (e.key === "Delete") {
        const selection = window.getSelection();
        if (selection && selection.isCollapsed && selection.rangeCount > 0) {
          const { startContainer: node, startOffset: offset } =
            selection.getRangeAt(0);
          let target: HTMLElement | null = null;

          if (
            node.nodeType === Node.TEXT_NODE &&
            offset === (node.textContent?.length ?? 0)
          ) {
            const next = node.nextSibling;
            if (
              next instanceof HTMLElement &&
              next.hasAttribute(MENTION_ATTR)
            )
              target = next;
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const next = node.childNodes[offset];
            if (
              next instanceof HTMLElement &&
              next.hasAttribute(MENTION_ATTR)
            )
              target = next;
          }

          if (target) {
            e.preventDefault();
            target.remove();
            syncToState();
          }
        }
      }
    },
    [
      insertMention,
      mentionOptions,
      onUploadMedia,
      selectedOptionIndex,
      showMention,
      syncToState,
      focusEditor,
    ]
  );

  const handleMentionOpenChange = useCallback((open: boolean) => {
    setShowMention(open);
    if (!open) {
      removeMarker(editorRef.current);
      editorRef.current?.focus();
    }
  }, []);

  return (
    <div className="relative flex-1 flex flex-col min-h-0 mt-2">
      <Popover open={showMention} onOpenChange={handleMentionOpenChange}>
        <PopoverAnchor asChild>
          <div
            ref={anchorRef}
            className="absolute w-0 h-0 pointer-events-none"
          />
        </PopoverAnchor>
        <PopoverContent
          className="w-64 p-0"
          side="bottom"
          align="start"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            focusEditor();
          }}
        >
          <Command>
            <CommandList>
              {mediaFiles.length > 0 && (
                <CommandGroup heading="Media files">
                  {mediaFiles.map((file) => (
                    <CommandItem
                      key={file.path}
                      className={cn(
                        selectedOptionIndex ===
                          mentionOptions.findIndex(
                            (option) =>
                              option.type === "media" && option.id === file.path
                          ) && "bg-accent text-accent-foreground"
                      )}
                      onMouseEnter={() => {
                        const index = mentionOptions.findIndex(
                          (option) =>
                            option.type === "media" && option.id === file.path
                        );
                        if (index >= 0) {
                          setSelectedOptionIndex(index);
                        }
                      }}
                      onSelect={() =>
                        insertMention(file.code || createMediaCode(file.name))
                      }
                    >
                      <ImageIcon className="h-4 w-4" />
                      {file.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {boards.length > 0 && (
                <CommandGroup heading="Boards">
                  {boards.map((board) => (
                    <CommandItem
                      key={board.id}
                      className={cn(
                        selectedOptionIndex ===
                          mentionOptions.findIndex(
                            (option) =>
                              option.type === "board" && option.id === board.id
                          ) && "bg-accent text-accent-foreground"
                      )}
                      onMouseEnter={() => {
                        const index = mentionOptions.findIndex(
                          (option) =>
                            option.type === "board" && option.id === board.id
                        );
                        if (index >= 0) {
                          setSelectedOptionIndex(index);
                        }
                      }}
                      onSelect={() =>
                        insertMention(board.code || createBoardCode(board.name))
                      }
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      {board.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {mediaFiles.length === 0 && boards.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No media or boards available
                </div>
              )}
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  className={cn(
                    selectedOptionIndex === mentionOptions.length - 1 &&
                      "bg-accent text-accent-foreground"
                  )}
                  onMouseEnter={() =>
                    setSelectedOptionIndex(mentionOptions.length - 1)
                  }
                  onSelect={() => {
                    setShowMention(false);
                    removeMarker(editorRef.current);
                    mentionRangeRef.current = null;
                    onUploadMedia();
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Upload media
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {!value && (
        <div className="absolute inset-0 px-3 py-2 text-muted-foreground pointer-events-none font-mono text-sm">
          {placeholder}
        </div>
      )}

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className={cn(
          "border-input focus-visible:border-ring focus-visible:ring-ring/50",
          "flex-1 min-h-0 w-full rounded-md border bg-transparent px-3 py-2 shadow-xs",
          "transition-[color,box-shadow] outline-none focus-visible:ring-[3px]",
          "overflow-y-auto whitespace-pre-wrap wrap-break-word",
          "font-mono text-sm"
        )}
        role="textbox"
        aria-multiline="true"
      />
    </div>
  );
};
